import { rm } from "@zenfs/core/promises";
import ky from "ky";
import { err, ok, okAsync, type Result, ResultAsync } from "neverthrow";
import { useAuthStore } from "@/stores/authStore";
import { refreshToken } from "./oauth";

// Define the type of the onProgress callback function
export type ProgressCallback = (progressEvent: {
	totalBatch: number;
	currentBatch: number;
}) => void;

const user = JSON.parse(localStorage.getItem("oauth.user") as string);

let token = localStorage.getItem("oauth.access_token");

const host = import.meta.env.VITE_APP_GITEA_HOST;
const userName = user?.name;
const email = user?.email;

export const getMediaURL = (owner: string, workspace: string) =>
	`${host}/api/v1/repos/${owner}/${workspace}/media`;

export type GiteaTag = {
	commit: {
		created: string;
		sha: string;
		url: string;
	};
	id: string;
	message: string;
	name: string;
	tarball_url: string;
	zipball_url: string;
};

export interface GiteaPermissions {
	admin: boolean;
	push: boolean;
	pull: boolean;
}

export type File = {
	content?: string;
	from_path?: string;
	operation: "create" | "update" | "delete";
	path: string;
	sha?: string;
};

export const giteaKy = ky.create({
	prefixUrl: `${import.meta.env.VITE_APP_GITEA_HOST}/api/v1`,
	timeout: 120000,
	hooks: {
		beforeRequest: [
			(request) => {
				if (!token) {
					token = localStorage.getItem("oauth.access_token");
				}
				request.headers.set("Authorization", `token ${token}`);
			},
		],
		beforeRetry: [
			() => {
				if (!token) {
					token = localStorage.getItem("oauth.access_token");
				}
			},
		],
		afterResponse: [
			async (request, _options, response) => {
				if (response.status === 401) {
					const authStore = useAuthStore();
					// Get a fresh token
					const res = await refreshToken();

					if (res.isErr()) {
						localStorage.clear();
						await rm("/", { force: true, recursive: true }).catch(() => {});
						window.location.reload();
						return;
					}

					token = res.value.accessToken;

					const user = localStorage.getItem("oauth.user");
					authStore.setUser(JSON.parse(user as string));

					request.headers.set("Authorization", `token ${token}`);

					return ky(request);
				}
			},
		],
	},
});

export function getLatestCommit({
	owner,
	workspace,
	branch = "main",
}: {
	owner: string;
	workspace: string;
	branch?: string;
}) {
	return ResultAsync.fromPromise(
		giteaKy
			.get(`repos/${owner}/${workspace}/branches/${branch}`, {
				headers: { "Cache-Control": "no-cache" },
			})
			.json<{ commit: { timestamp: string; id: string } }>()
			.then((res) => res.commit),
		(e) => new Error("Error while fetching commit info", { cause: e }),
	);
}

export type CommitInfo = {
	sha: string;
	commit: {
		author: { name: string };
		message: string;
	};
	files: { filename: string; status: string }[];
	created: string;
};

export function getCommits({
	owner,
	workspace,
	ref = "main",
	path,
	page = 1,
	lastCommit = "",
}: {
	owner: string;
	workspace: string;
	ref?: string;
	path?: string;
	page?: number;
	lastCommit?: string;
}) {
	const searchParams = new URLSearchParams();
	searchParams.set("sha", ref);
	searchParams.set("stat", "false");
	searchParams.set("verification", "false");
	searchParams.set("limit", "50");
	searchParams.set("page", `${page}`);
	if (path) {
		searchParams.set("path", path);
	}

	return ResultAsync.fromPromise(
		giteaKy
			.get(`repos/${owner}/${workspace}/commits`, {
				searchParams,
				headers: { "Cache-Control": "no-cache" },
			})
			.json<CommitInfo[]>(),
		(e) => new Error("Error while fetching commits", { cause: e }),
	).andThen((commits): ResultAsync<typeof commits, Error> => {
		const lastCommitIndex = lastCommit
			? commits.findIndex(({ sha }) => sha === lastCommit)
			: -1;

		if (lastCommitIndex !== -1) {
			return okAsync([...commits.slice(0, lastCommitIndex)]);
		}

		// TODO: Cache Commits
		if (commits.length !== 50 || page > 3) {
			return okAsync(commits);
		}

		return getCommits({ owner, workspace, ref, path, page: page + 1 }).map(
			(res) => [...commits, ...res],
		);
	});
}

export const getContent = <T>({
	owner,
	workspace,
	filePath,
	ref = "main",
}: {
	owner: string;
	workspace: string;
	filePath: string;
	ref?: string;
}) =>
	ResultAsync.fromPromise(
		giteaKy
			.get(`repos/${owner}/${workspace}/contents${filePath}?ref=${ref}`, {
				headers: { "Cache-Control": "no-cache" },
			})
			.json<T>(),
		(err) => new Error("Error while fetching content", { cause: err }),
	);

export const loadFile = ({
	owner,
	workspace,
	filePath,
	ref = "main",
}: {
	owner: string;
	workspace: string;
	filePath: string;
	ref?: string;
}) =>
	ResultAsync.fromPromise(
		giteaKy.get(`repos/${owner}/${workspace}/media${filePath}?ref=${ref}`, {
			headers: { "Cache-Control": "no-cache" },
		}),
		(err) => new Error("Error while fetching file", { cause: err }),
	);

const calculateFileSize = (content: string) => atob(content).length / 10 ** 6;

export interface FileInfo {
	operation: "create" | "update" | "delete";
	path: string;
	content?: string;
	sha?: string;
}

/**
 * Creates or updates multiple files in the repository.
 */
export const createOrUpdateMultipleFiles = async ({
	owner,
	workspace,
	files,
	message,
	branch = "main",
	onUploadProgress,
}: {
	owner: string;
	workspace: string;
	files: FileInfo[];
	message: string;
	branch?: string;
	onUploadProgress?: ProgressCallback;
}): Promise<Result<undefined, Error>> => {
	const maxRetries = 3;
	const maxFileSize = 25; // MB;

	const contentFiles = files.filter(
		(file) => file.operation === "update" || file.operation === "create",
	);

	const sortedFiles = [...contentFiles]?.sort(
		({ content: content1 }, { content: content2 }) =>
			calculateFileSize(content1 as string) -
			calculateFileSize(content2 as string),
	);

	const batches: FileInfo[][] = [];
	let currentBatch: FileInfo[] = [];
	let currentBatchSize = 0;

	// Group files into batches based on file size
	for (const file of sortedFiles) {
		const fileSize = calculateFileSize(file.content as string);
		if (currentBatchSize + fileSize > maxFileSize) {
			if (currentBatch.length) {
				batches.push(currentBatch);
			}
			currentBatch = [file];
			currentBatchSize = fileSize;
		} else {
			currentBatch.push(file);
			currentBatchSize += fileSize;
		}
	}

	if (currentBatch.length) {
		batches.push(currentBatch);
	}

	const deleteFiles = files.filter((file) => file.operation === "delete");

	if (deleteFiles.length) {
		batches.push(deleteFiles);
	}

	for (const [index, batch] of batches.entries()) {
		const res = await ResultAsync.fromPromise(
			giteaKy.post(`repos/${owner}/${workspace}/contents?ref=${branch}`, {
				headers: {
					Authorization: `token ${token}`,
					"Content-Type": "application/json",
				},
				json: {
					files: batch,
					branch,
					author: {
						name: userName,
						email,
					},
					committer: {
						name: userName,
						email,
					},
					message: `${message} batch ${index + 1}`,
					signoff: true,
				},
				retry: {
					limit: maxRetries,
				},
				timeout: 300000,
				hooks: {
					beforeRetry: [
						({ retryCount }) => {
							console.error(
								`Error processing batch ${
									index + 1
								} of files. Retrying... (${retryCount}/${maxRetries})`,
							);
						},
					],
				},
			}),
			(err) =>
				new Error(
					`Failed to process batch ${
						index + 1
					} of files after ${maxRetries} retries.`,
					{ cause: err },
				),
		);

		if (res.isOk()) {
			if (onUploadProgress) {
				onUploadProgress({
					totalBatch: batches.length,
					currentBatch: index + 1,
				});
			}
		} else {
			return err<undefined, Error>(res.error);
		}
	}

	return ok<undefined, Error>(undefined);
};

export const deleteProject = async ({
	owner,
	workspace,
	name,
	message,
	branch = "main",
}: {
	owner: string;
	workspace: string;
	name: string;
	message: string;
	branch?: string;
}): Promise<Result<undefined, Error>> => {
	const maxRetries = 3;

	const files = (await giteaKy
		.get(
			`repos/${owner}/${workspace}/contents/projects/${name}?ref=${branch}`,
			{
				headers: {
					Authorization: `token ${token}`,
					"Content-Type": "application/json",
				},
			},
		)
		.json()) as File[];

	for (const file of files) {
		const res = await ResultAsync.fromPromise(
			giteaKy.delete(`repos/${owner}/${workspace}/contents/${file.path}`, {
				headers: {
					Authorization: `token ${token}`,
					"Content-Type": "application/json",
				},
				json: {
					branch,
					author: {
						name: userName,
						email,
					},
					committer: {
						name: userName,
						email,
					},
					message,
					sha: file.sha,
					signoff: true,
				},
				retry: {
					limit: maxRetries,
				},
				timeout: 300000,
				hooks: {
					beforeRetry: [
						({ retryCount }) => {
							console.error(
								`Error deleting project files. Retrying... (${retryCount}/${maxRetries})`,
							);
						},
					],
				},
			}),
			(err) =>
				new Error(`Failed to delete project after ${maxRetries} retries.`, {
					cause: err,
				}),
		);

		if (!res.isOk()) {
			return err<undefined, Error>(res.error);
		}
	}

	return ok<undefined, Error>(undefined);
};

type GiteaRepo = {
	id: number;
	owner: { login: string };
	name: string;
	full_name: string;
	updated_at: string;
};

export const getUserWorkspaces = (): ResultAsync<GiteaRepo[], Error> => {
	const fetchAllRepos = async (): Promise<GiteaRepo[]> => {
		const allRepos: GiteaRepo[] = [];
		const limit = 10;
		let page = 1;

		while (true) {
			const repos = await giteaKy
				.get("user/repos", {
					searchParams: {
						page: String(page),
						limit: String(limit),
					},
					headers: {
						"Cache-Control": "no-cache",
					},
				})
				.json<GiteaRepo[]>();

			allRepos.push(...repos);

			if (repos.length < limit) {
				break;
			}

			page++;
		}

		return allRepos;
	};

	return ResultAsync.fromPromise(
		fetchAllRepos(),
		(error) =>
			new Error("Error while fetching user workspaces", { cause: error }),
	);
};

export const getProjectPublishedVersions = (
	owner: string,
	workspace: string,
) => {
	const fetchTags = (
		page: number,
		allTags: GiteaTag[] = [],
	): Promise<GiteaTag[]> => {
		return giteaKy
			.get(`repos/${owner}/${workspace}/tags`, {
				searchParams: {
					page: page.toString(),
					limit: "50",
				},
				headers: {
					"Cache-Control": "no-cache",
				},
			})
			.json<GiteaTag[]>()
			.then((tags) => {
				if (tags.length === 50) {
					return fetchTags(page + 1, allTags.concat(tags));
				}

				return allTags.concat(tags);
			});
	};

	return ResultAsync.fromPromise(
		fetchTags(1),
		(e) => new Error("Error while fetching tags.", { cause: e }),
	);
};

export const publishProject = async (
	owner: string,
	workspace: string,
	tagName: string,
	repoId: number,
	urls?: string[],
) => {
	const maxRetries = 3;
	const createTagResult = await ResultAsync.fromPromise(
		giteaKy.post(`repos/${owner}/${workspace}/tags`, {
			headers: {
				"Cache-Control": "no-cache",
			},
			json: {
				message: `Created new tag ${tagName}`,
				tag_name: tagName,
			},
			retry: {
				limit: maxRetries,
			},
			hooks: {
				beforeRetry: [
					({ retryCount }) => {
						console.error(
							`Error creating ${tagName} tag. Retrying... (${retryCount}/${maxRetries})`,
						);
					},
				],
			},
		}),
		(err) =>
			new Error(
				`Failed to create ${tagName} tag after ${maxRetries} retries.`,
				{ cause: err },
			),
	);

	if (createTagResult.isErr()) {
		console.error(createTagResult.error.message);
		return;
	}

	console.log(`Successfully created tag: ${tagName}`);

	const createReleaseResult = await ResultAsync.fromPromise(
		giteaKy
			.post(`repos/${owner}/${workspace}/releases`, {
				headers: {
					"Cache-Control": "no-cache",
				},
				json: {
					tag_name: tagName,
					name: `Release ${tagName}`,
					body: `Release ${tagName}`,
					draft: false,
					prerelease: false,
				},
				retry: {
					limit: maxRetries,
				},
				hooks: {
					beforeRetry: [
						({ retryCount }) => {
							console.error(
								`Error creating release for ${tagName}. Retrying... (${retryCount}/${maxRetries})`,
							);
						},
					],
				},
			})
			.json(),
		(err) =>
			new Error(
				`Failed to create release for ${tagName} after ${maxRetries} retries.`,
				{ cause: err },
			),
	);

	if (createReleaseResult.isErr()) {
		console.error(createReleaseResult.error.message);
		return;
	}

	// Step 3: Post Whitelist URLs (if provided)
	let retryCount = 0;
	let success = false;

	while (retryCount < maxRetries && !success) {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_APP_WHITELIST_API}/${repoId}/${tagName}/allowed-urls`,
				{
					method: "POST",
					headers: {
						Authorization: `token ${token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ urls }),
				},
			);

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			console.log("Successfully posted URLs");
			success = true;
		} catch (error) {
			retryCount++;
			console.error(
				`Error posting URLs to release ${tagName}. Retrying... (${retryCount}/${maxRetries})`,
				error,
			);

			if (retryCount >= maxRetries) {
				console.error(
					`Failed to post URLs to release ${tagName} after ${maxRetries} retries.`,
				);
			}
		}
	}
};

export const getAllowedUrls = (
	repoId: number,
	tagName: string,
): ResultAsync<string[], Error> => {
	return ResultAsync.fromPromise(
		fetch(
			`${import.meta.env.VITE_APP_WHITELIST_API}/${repoId}/${tagName}/allowed-urls`,
			{
				method: "GET",
				headers: {
					Authorization: `token ${token}`,
					"Content-Type": "application/json",
				},
			},
		).then(async (response) => {
			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}
			const data = await response.json();
			return data.data;
		}),
		(error) => new Error("Error while fetching allowed URLs", { cause: error }),
	);
};

export const updateAllowedUrls = (
	repoId: number,
	tagName: string,
	urls: string[],
) => {
	return ResultAsync.fromPromise(
		fetch(
			`${import.meta.env.VITE_APP_WHITELIST_API}/${repoId}/${tagName}/allowed-urls`,
			{
				method: "POST",
				headers: {
					Authorization: `token ${token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ urls }),
			},
		),
		(err) =>
			new Error(`Failed to update allowed URLs for ${tagName}`, { cause: err }),
	);
};

export const deleteAllowedUrl = (
	repoId: number,
	tagName: string,
	url: string,
) => {
	return ResultAsync.fromPromise(
		fetch(
			`${import.meta.env.VITE_APP_WHITELIST_API}/${repoId}/${tagName}/allowed-urls`,
			{
				method: "DELETE",
				headers: {
					Authorization: `token ${token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ url }),
			},
		),
		(err) =>
			new Error(`Failed to delete allowed URL ${url} for ${tagName}`, {
				cause: err,
			}),
	);
};

export const addUrlToAllVersions = (
	repoId: number,
	url: string,
	latestTag: string,
) => {
	return ResultAsync.fromPromise(
		fetch(`${import.meta.env.VITE_APP_WHITELIST_API}/${repoId}/allowed-urls`, {
			method: "POST",
			headers: {
				Authorization: `token ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ url, latestTag }),
		}),
		(err) => new Error(`Failed to add URL ${url}`, { cause: err }),
	);
};

export const removeUrlFromAllVersions = (
	repoId: number,
	url: string,
	latestTag: string,
) => {
	return ResultAsync.fromPromise(
		fetch(`${import.meta.env.VITE_APP_WHITELIST_API}/${repoId}/allowed-urls`, {
			method: "DELETE",
			headers: {
				Authorization: `token ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ url, latestTag }),
		}),
		(err) => new Error(`Failed to remove URL ${url}`, { cause: err }),
	);
};

export function getTree({
	owner,
	workspace,
	sha,
	page = 1,
}: {
	owner: string;
	workspace: string;
	sha: string;
	page?: number;
}) {
	return ResultAsync.fromPromise(
		giteaKy
			.get(
				`repos/${owner}/${workspace}/git/trees/${sha}?page=${page}&recursive=true`,
				{
					headers: { "Cache-Control": "no-cache" },
				},
			)
			.json<{
				tree: { path: string; type: string; sha: string }[];
				truncated: boolean;
				page: number;
			}>()
			.then((res) => res),
		(e) => new Error("Error while fetching commit info", { cause: e }),
	);
}

export type TreeEntry = {
	path: string;
	type: string;
	sha: string;
	mode?: string;
	size?: number;
	url?: string;
	lastModified?: string;
};

export function getTreeFull({
	owner,
	workspace,
	sha,
}: {
	owner: string;
	workspace: string;
	sha: string;
}) {
	const getCommitTree = (page = 1): ResultAsync<TreeEntry[], Error> => {
		return getTree({
			owner,
			workspace,
			sha,
			page,
		}).andThen((result) => {
			// TODO: Parallel
			const { tree, truncated, page } = result;
			if (!tree) {
				return ok([]);
			}
			if (truncated) {
				return getCommitTree(page + 1).andThen((res) => ok([...tree, ...res]));
			}

			return ok(tree);
		});
	};
	const commitTree = getCommitTree();

	return commitTree;
}

export function applyPatch({
	owner,
	workspace,
	sha,
	patch,
}: {
	owner: string;
	workspace: string;
	sha: string;
	patch: string;
}) {
	return ResultAsync.fromPromise(
		giteaKy
			.post(`repos/${owner}/${workspace}/diffpatch`, {
				headers: {
					"Cache-Control": "no-cache",
				},
				json: {
					content: patch,
					sha,
				},
			})
			.json<{ sha: string }>(),
		(e) => new Error("Error while applying patch", { cause: e }),
	);
}

// TODO:
export async function lfsUpload(
	{ blob, oid, size }: { blob: Blob; oid: string; size: number },
	{
		owner,
		workspace,
	}: {
		owner: string;
		workspace: string;
	},
) {
	const _path = `--- /dev/null
+++ b/assets/test1.png
@@ -0,0 +1,3 @@
+version https://git-lfs.github.com/spec/v1
+oid sha256:f5ba45490d9a62c2d9112143ba49bff49e25567a51a3523d1c8f7b0ad63cf6f4
+size 3229
\ No newline at end of file`;

	return ResultAsync.fromPromise(
		giteaKy.put(`${owner}/${workspace}.git/info/lfs/objects/${oid}/${size}`, {
			prefixUrl: `${import.meta.env.VITE_APP_GITEA_HOST}`,
			headers: {
				"Content-Type": "application/octet-stream",
			},
			body: blob,
		}),
		(e) => new Error("Error while uploading", { cause: e }),
	);
}

export function getUserWorkspace({
	owner,
	workspace,
}: {
	owner: string;
	workspace: string;
}) {
	return ResultAsync.fromPromise(
		giteaKy
			.get(`repos/${owner}/${workspace}`, {
				headers: { "Cache-Control": "no-cache" },
			})
			.json<{
				id: number;
				permissions: {
					pull: boolean;
					push: boolean;
					admin: boolean;
				};
			}>()
			.then((res) => res),
		(e) => new Error("Error while fetching commit info", { cause: e }),
	);
}
